import { TOTAL_VENIT_LUNAR_REALIZAT } from '../../components/EntryTable';
import { IClient } from '../interfaces/client.interface';
import { IEntry } from '../interfaces/entry.interface';
import { IRequest } from '../interfaces/request.interface';
import { extractDateFromCNP } from './dates';
import { Paragraph, PDFStaticBuilderProps } from './pdf-builder';
import htmlToPdfmake from 'html-to-pdfmake';

const DEFAULT_VALUE = '-';

export const BuildPDFConfig = (
  config: PDFStaticBuilderProps,
  request: IRequest,
  entries: IEntry[],
  client: IClient,
  company_name: string,
) => {
  const years = Array.from(new Set(entries.map((el) => el.year))).sort();
  // Step 1: Group the objects by year using a Map
  const groupedByYear = entries.reduce((acc, curr) => {
    const { year } = curr;
    if (!acc.has(year)) {
      acc.set(year, []);
    }
    acc.get(year).push(curr);
    return acc;
  }, new Map());

  // Step 2: Create an array of 13 properties for each key
  const result: any = {};

  groupedByYear.forEach((entries: IEntry[], year) => {
    const yearData: any = {};

    entries.forEach((entry) => {
      const { month, properties } = entry;

      if (Array.isArray(properties)) {
        properties.forEach(({ key, value }) => {
          if (!yearData[key]) {
            yearData[key] = Array.from({ length: 13 }, (_, index) => ({
              text: index === 0 ? key : config.settings?.empty_months || '0',
              style:
                index === 0
                  ? key === TOTAL_VENIT_LUNAR_REALIZAT
                    ? 'tableLeftBold'
                    : 'tableLeft'
                  : 'tableCell',
            }));
          }
          yearData[key][month] = { text: value || '0', style: 'tableCell' };
        });
      }
    });

    result[year] = Object.values(yearData);
  });

  // Optionally, sort the properties for each year to ensure the special key is last
  for (const year in result) {
    result[year].sort((a: any, b: any) => {
      if (a[0].text === TOTAL_VENIT_LUNAR_REALIZAT) return 1;
      if (b[0].text === TOTAL_VENIT_LUNAR_REALIZAT) return -1;
      return 0;
    });
  }

  const requestInfo: Partial<IRequest> & {
    client_name: string;
    company_name: string;
    birthdate: string;
  } = {
    ...request,
    client_name: client?.name,
    company_name: company_name,
    birthdate: extractDateFromCNP(request.cnp),
  };

  const hasHeader = config.header.image !== '' && config.header.rightText !== '';

  if (config.header.rightText) {
    config.header.rightText = config.header.rightText?.replace(
      /\{([^}]+)\}/g,
      (match, key) => requestInfo[key] || match,
    );
  }

  if (config.footer.length) {
    config.footer = config.footer.map((el: string) => {
      return el.replace(/\{([^}]+)\}/g, (_, key) => requestInfo[key] || DEFAULT_VALUE);
    });
  }

  if (config.first_page.client_information.length) {
    config.first_page.client_information = config.first_page.client_information.map(
      (el: string) => {
        return htmlToPdfmake(
          el.replace(/\{([^}]+)\}/g, (_, key) => requestInfo[key] || DEFAULT_VALUE),
        );
      },
    );
  }

  if (config.first_page.title) {
    config.first_page.title = config.first_page.title.replace(
      /\{([^}]+)\}/g,
      (_, key) => requestInfo[key] || DEFAULT_VALUE,
    );
  }

  if (config.first_page.paragraphs.length) {
    config.first_page.paragraphs = config.first_page.paragraphs.map((el: Paragraph) => {
      return {
        text:
          el.type === 'html'
            ? htmlToPdfmake(
                el.text.replace(/\{([^}]+)\}/g, (_, key) => requestInfo[key] || DEFAULT_VALUE),
              )
            : el.text.replace(/\{([^}]+)\}/g, (_, key) => requestInfo[key] || DEFAULT_VALUE),
        alignment: el.alignment,
      };
    });
  }

  if (config.after_tables.length) {
    config.after_tables = config.after_tables.map((el: Paragraph) => {
      return {
        text:
          el.type === 'html'
            ? htmlToPdfmake(
                el.text.replace(/\{([^}]+)\}/g, (_, key) => requestInfo[key] || DEFAULT_VALUE),
              )
            : el.text.replace(/\{([^}]+)\}/g, (_, key) => requestInfo[key] || DEFAULT_VALUE),
        alignment: el.alignment,
      };
    });
  }

  if (config.last_page.length) {
    config.last_page = config.last_page.map((el: Paragraph) => {
      return {
        text: el.text.replace(/\{([^}]+)\}/g, (_, key) => requestInfo[key] || DEFAULT_VALUE),
        alignment: el.alignment,
      };
    });
  }

  const dd = {
    defaultStyle: {
      font: 'Roboto',
    },
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [40, hasHeader ? 80 : 40, 40, config.footer.length * 8 + 50],
    images: {
      logo: config.header.image,
    },
    header: hasHeader && {
      margin: [40, 40, 40, 40],
      columns: [
        config.header.image && {
          image: 'logo',
          width: 100,
        },
        {
          width: '*',
          text: '',
        },
        {
          alignment: 'right',
          text: config.header.rightText || '',
          style: 'p',
        },
      ],
    },
    footer: function (currentPage, totalPages) {
      return [
        {
          margin: [0, 10, 0, 0],
          text: '',
        },
        ...config.footer.map((el) => {
          return {
            margin: [0, 1, 0, 0],
            text: el,
            style: 'footerText',
            alignment: 'center',
          };
        }),
        {
          margin: [0, 2, 0, 0],
          text: `${currentPage}/${totalPages}`,
          style: 'footerPages',
          alignment: 'center',
        },
      ];
    },
    content: [
      {
        stack: [
          ...config.first_page.client_information.map((el) => {
            return el;
          }),
        ],
        style: { fontSize: 10 },
        margin: [0, 0, 0, 10],
      },
      {
        text: config.first_page.title || '',
        alignment: 'center',
        style: { fontSize: 10, bold: true },
      },
      ...config.first_page.paragraphs.map((el) => {
        return {
          text: el.text,
          style: ['extraMargin', el.alignment],
          preserveLeadingSpaces: true,
        };
      }),
      [...years].map((year) => {
        return [
          {
            table: {
              headerRows: 2,
              dontBreakRows: true,
              keepWithHeaderRows: true,
              widths: [100, 45, 45, 40, 40, 35, 45, 35, 50, 55, 50, 50, 50],
              body: [
                [
                  {
                    text: '',
                    colSpan: 1,
                    style: 'tableCell',
                    alignment: 'center',
                  },
                  {
                    text: `ANUL ${year}`,
                    style: 'tableCell',
                    alignment: 'left',
                    colSpan: 12,
                    bold: true,
                  },
                  {},
                  {},
                  {},
                  {},
                  {},
                  {},
                  {},
                  {},
                  {},
                  {},
                  {},
                ],
                [
                  { text: '' },
                  { text: 'Ianuarie', style: 'tableHead' },
                  { text: 'Februarie', style: 'tableHead' },
                  { text: 'Martie', style: 'tableHead' },
                  { text: 'Aprilie', style: 'tableHead' },
                  { text: 'Mai', style: 'tableHead' },
                  { text: 'Iunie', style: 'tableHead' },
                  { text: 'Iulie', style: 'tableHead' },
                  { text: 'August', style: 'tableHead' },
                  { text: 'Septembrie', style: 'tableHead' },
                  { text: 'Octombrie', style: 'tableHead' },
                  { text: 'Noiembrie', style: 'tableHead' },
                  { text: 'Decembrie', style: 'tableHead' },
                ],
                ...result[year],
              ],
            },
          },
          {
            text: '',
            style: 'extraMargin',
          },
        ];
      }),
      {
        text: '',
        style: 'extraMargin',
      },
      ...config.after_tables.map((el) => {
        return {
          text: el.text,
          style: [el.alignment],
          preserveLeadingSpaces: true,
        };
      }),
      {
        pageBreak: 'before',
        text: '',
        style: 'p',
      },
      ...config.last_page.map((el) => {
        return {
          text: el.text,
          style: [el.alignment],
          preserveLeadingSpaces: true,
        };
      }),
      {
        text: ``,
        margin: [0, 30],
      },
    ],
    styles: {
      p: {
        margin: [0, 5],
        fontSize: 10,
      },
      extraMargin: {
        margin: [0, 10],
      },
      center: {
        margin: [0, 5],
        fontSize: 10,
        alignment: 'center',
      },
      right: {
        margin: [0, 5],
        fontSize: 10,
        alignment: 'right',
      },
      left: {
        margin: [0, 5],
        fontSize: 10,
        alignment: 'justify',
      },
      tableCell: {
        font: 'Roboto',
        fontSize: 8,
        alignment: 'right',
        margin: [0, 5, 0, 0],
      },
      tableHead: {
        font: 'Roboto',
        fontSize: 8,
        bold: true,
        alignment: 'center',
        margin: [2, 2],
      },
      tableLeftBold: {
        font: 'Roboto',
        fontSize: 8,
        alignment: 'left',
        margin: [0, 4, 0, 0],
        bold: true,
      },
      tableLeft: {
        font: 'Roboto',
        fontSize: 8,
        alignment: 'left',
        margin: [0, 4, 0, 0],
      },
      footerText: {
        fontSize: 8,
        color: 'gray',
      },
      footerPages: {
        fontSize: 8,
      },
      footerSite: {
        fontSize: 8,
        color: 'blue',
        decoration: 'underline',
      },
    },
  };

  return dd;
};

export const BuildEntriesDetailsConfig = (
  config: PDFStaticBuilderProps,
  request: IRequest,
  entries: IEntry[],
) => {
  const entriesWithDetails = entries.filter((entries) => entries.details !== null);
  const hasHeader = config.header.image !== '' && config.header.rightText !== '';

  const dd = {
    defaultStyle: {
      font: 'Roboto',
    },
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [40, hasHeader ? 80 : 40, 40, config.footer.length * 8 + 50],
    images: {
      logo: config.header.image,
    },
    header: hasHeader && {
      margin: [40, 40, 40, 40],
      columns: [
        config.header.image && {
          image: 'logo',
          width: 100,
        },
        {
          width: '*',
          text: '',
        },
        {
          alignment: 'right',
          text: config.header.rightText || '',
          style: 'p',
        },
      ],
    },
    footer: function (currentPage, totalPages) {
      return [
        {
          margin: [0, 10, 0, 0],
          text: '',
        },
        ...config.footer.map((el) => {
          return {
            margin: [0, 1, 0, 0],
            text: el,
            style: 'footerText',
            alignment: 'center',
          };
        }),
        {
          margin: [0, 2, 0, 0],
          text: `${currentPage}/${totalPages}`,
          style: 'footerPages',
          alignment: 'center',
        },
      ];
    },
    content: [
      {
        text: `Cerere: ${request.name} - ${request.cnp}`,
        style: 'center',
      },
      entriesWithDetails.length
        ? {
            text: `Urmatoarele luni adaugate au avut urmatoarele observatii:`,
            style: 'center',
          }
        : [],
      ...[
        entriesWithDetails.map((entry) => [
          {
            text: `Luna: ${entry.month} / ${entry.year}`,
            style: 'p',
          },
          {
            text: `${entry.details}`,
            style: 'p',
          },
          {
            table: {
              widths: ['*'],
              body: [[' '], [' ']],
            },
            layout: {
              hLineWidth: function (i: any, node: any) {
                return i === 0 || i === node.table.body.length ? 0 : 1;
              },
              vLineWidth: function () {
                return 0;
              },
            },
          },
        ]),
      ],
    ],

    styles: {
      p: {
        margin: [0, 5],
        fontSize: 10,
      },
      extraMargin: {
        margin: [0, 10],
      },
      center: {
        margin: [0, 5],
        fontSize: 10,
        alignment: 'center',
      },
      tableCell: {
        font: 'Roboto',
        fontSize: 8,
        alignment: 'right',
        margin: [0, 5, 0, 0],
      },
      tableHead: {
        font: 'Roboto',
        fontSize: 8,
        bold: true,
        alignment: 'center',
        margin: [2, 2],
      },
      tableLeftBold: {
        font: 'Roboto',
        fontSize: 8,
        alignment: 'left',
        margin: [0, 4, 0, 0],
        bold: true,
      },
      tableLeft: {
        font: 'Roboto',
        fontSize: 8,
        alignment: 'left',
        margin: [0, 4, 0, 0],
      },
      footerText: {
        fontSize: 8,
        color: 'gray',
      },
      footerPages: {
        fontSize: 8,
      },
      footerSite: {
        fontSize: 8,
        color: 'blue',
        decoration: 'underline',
      },
    },
  };

  return dd;
};
